'use client';

import { useSyncExternalStore } from 'react';

/**
 * supremeRequestContext — sync the SI session into cookies for org scoping.
 *
 * Headers only ride on `fetch`, which left preview `<iframe src>` loads,
 * `EventSource` streams, and other resource loads unscoped (they 401'd in
 * enforce mode). Cookies ride on EVERY same-origin request, so we mirror two
 * values from the SDK's `useCreditSystem` into cookies:
 *
 *   - `supreme-token`  : the SI access token (so the Railway proxy can validate
 *                        the session on every request type, not just fetch).
 *   - `supreme-org-id` : `creditSystem.selectedOrganization.id` — the org the
 *                        SI parent currently has selected.
 *
 * The proxy reads these, validates the token, injects the trusted org to the
 * daemon as `X-Supreme-Org-Id`, and strips both cookies before forwarding.
 * This only works because the app is deployed SAME-SITE with its parent
 * (`*.supremegroup.ai`), so the cookies are sent inside the iframe.
 *
 * Source of truth lives in customizations/supreme-sdk/; apply.mjs copies it
 * into apps/web/src/supreme/ at build time so the sidecar stays rebase-safe.
 */

export const SUPREME_TOKEN_COOKIE = 'supreme-token';
export const SUPREME_ORG_COOKIE = 'supreme-org-id';

function writeCookie(name: string, value: string): void {
  // SameSite=None; Secure so the cookie is sent on requests made from inside the
  // SI iframe (an embedded/third-party browsing context). Lax was not reliably
  // sent there, which left /api/projects without the token. Requires HTTPS.
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=None; Secure`;
}

function clearCookie(name: string): void {
  document.cookie = `${name}=; path=/; Max-Age=0; SameSite=None; Secure`;
}

/**
 * Mirror the access token + selected org into cookies (or clear them). Called
 * by SupremeAuthProvider whenever either changes. No-op during SSR.
 */
export function syncSupremeCookies(
  token: string | null | undefined,
  organizationId: string | null | undefined,
): void {
  if (typeof document === 'undefined') return;
  const t = typeof token === 'string' ? token.trim() : '';
  const org = typeof organizationId === 'string' ? organizationId.trim() : '';
  lastSyncedToken = t;
  lastSyncedOrg = org;
  // The org claim is per-tab module state (header on fetches, query on the
  // preview iframe URL) — it must track the tab's own org even while hidden;
  // only the browser-SHARED cookie jar is visibility-gated below.
  setSupremeOrgClaim(org || null);
  installVisibilityResync();
  // SI-278: only the VISIBLE tab owns the shared cookie jar. Background tabs
  // re-render too (SDK token refresh, deep-link postMessages) and used to
  // clobber the visible tab's org mid-session — its next preview <iframe src>
  // load (cookie-scoped; headers cannot ride an iframe request) then hit the
  // daemon org guard and rendered PROJECT_NOT_FOUND until a manual refresh
  // re-mirrored the org. Hidden tabs now defer; the visibility listener
  // flushes their values the moment they become the visible tab.
  if (document.visibilityState === 'hidden') return;
  flushSupremeCookies();
}

let lastSyncedToken = '';
let lastSyncedOrg = '';
let visibilityResyncInstalled = false;

function flushSupremeCookies(): void {
  if (lastSyncedToken) writeCookie(SUPREME_TOKEN_COOKIE, lastSyncedToken);
  else clearCookie(SUPREME_TOKEN_COOKIE);
  if (lastSyncedOrg) writeCookie(SUPREME_ORG_COOKIE, lastSyncedOrg);
  else clearCookie(SUPREME_ORG_COOKIE);
}

function installVisibilityResync(): void {
  if (visibilityResyncInstalled || typeof window === 'undefined') return;
  visibilityResyncInstalled = true;
  const resync = () => {
    if (document.visibilityState !== 'hidden') flushSupremeCookies();
  };
  document.addEventListener('visibilitychange', resync);
  // Two visible windows side by side still contend for the jar; following
  // focus keeps the window the user is interacting with correct.
  window.addEventListener('focus', resync);
}

/**
 * Per-request org claim (SI-275).
 *
 * The cookies above live in ONE browser-wide jar. A multi-org user with two
 * instance tabs open has both tabs re-mirroring THEIR org into the same
 * cookie — last writer wins, so every tab's requests get scoped to whichever
 * org wrote last, and refreshes flip between orgs.
 *
 * Fix: each tab also stamps its OWN selected org onto every same-origin
 * /api fetch as the `x-supreme-org-claim` header. The Railway proxy verifies
 * the claim against the validated token's org memberships (exactly like the
 * cookie claim) before trusting it, and prefers it over the shared cookie.
 * The cookie remains the fallback for <iframe src> loads and EventSource
 * streams, which cannot carry headers.
 */
export const SUPREME_ORG_CLAIM_HEADER = 'x-supreme-org-claim';
export const SUPREME_ORG_CLAIM_PARAM = 'si-org-claim';
export const SUPREME_TOKEN_HEADER = 'x-supreme-token';

let currentOrgClaim: string | null = null;
let fetchPatched = false;
let anchorClaimInstalled = false;

/**
 * Org claim as a query-string suffix for the preview <iframe src> (SI-278) —
 * iframe document requests cannot carry headers, and the shared cookie can
 * hold another tab's org. Returns '&si-org-claim=<org>' for appending to a
 * URL that already has a query string, or '' when no org is selected. The
 * proxy verifies the claim against the validated token's org memberships
 * (same rule as the header claim) and strips the param before forwarding.
 */
export function supremeOrgClaimQuery(): string {
  return currentOrgClaim
    ? `&${SUPREME_ORG_CLAIM_PARAM}=${encodeURIComponent(currentOrgClaim)}`
    : '';
}

export function setSupremeOrgClaim(organizationId: string | null): void {
  const next = organizationId && organizationId.trim() ? organizationId.trim() : null;
  const changed = next !== currentOrgClaim;
  currentOrgClaim = next;
  installSupremeOrgClaimFetch();
  installSupremeAnchorClaim();
  if (changed) {
    // Deferred: this setter runs during the auth provider's render; notifying
    // useSyncExternalStore subscribers synchronously would schedule updates
    // mid-render. A microtask lands after the commit.
    queueMicrotask(notifyOrgClaimListeners);
  }
}

/**
 * Reactive access to the tab's org claim. URLs memoized during render (the
 * preview iframe src, see apply.mjs section 11) must re-derive when the claim
 * resolves or changes — reading module state inside useMemo would freeze the
 * claim captured at first render (empty before auth resolves, stale across an
 * org switch) and reintroduce the wrong-org PROJECT_NOT_FOUND failure.
 */
const orgClaimListeners = new Set<() => void>();

function notifyOrgClaimListeners(): void {
  for (const listener of orgClaimListeners) listener();
}

export function subscribeSupremeOrgClaim(listener: () => void): () => void {
  orgClaimListeners.add(listener);
  return () => orgClaimListeners.delete(listener);
}

export function getSupremeOrgClaim(): string | null {
  return currentOrgClaim;
}

export function useSupremeOrgClaim(): string | null {
  return useSyncExternalStore(subscribeSupremeOrgClaim, getSupremeOrgClaim, () => null);
}

/** Pure form of {@link supremeOrgClaimQuery} for reactive callers. */
export function supremeOrgClaimQueryFor(claim: string | null): string {
  return claim ? `&${SUPREME_ORG_CLAIM_PARAM}=${encodeURIComponent(claim)}` : '';
}

/**
 * SI-277: downloads and "open in new tab" links are <a href> NAVIGATIONS —
 * like the preview iframe they carry no headers, so a foreign org in the
 * shared cookie jar made the daemon guard 404 them and the browser reported
 * "File wasn't available on site". Rewrite same-origin /api anchor hrefs at
 * click time (capture phase, before the default navigation reads the href)
 * to carry this tab's org as the si-org-claim query param — the proxy
 * verifies it exactly like the header claim and strips it before forwarding.
 */
function installSupremeAnchorClaim(): void {
  if (anchorClaimInstalled || typeof document === 'undefined') return;
  anchorClaimInstalled = true;
  document.addEventListener(
    'click',
    (ev) => {
      if (!currentOrgClaim) return;
      const target = ev.target as Element | null;
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('blob:') || href.startsWith('data:')) return;
      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        if (!(url.pathname === '/api' || url.pathname.startsWith('/api/'))) return;
        if (url.searchParams.has(SUPREME_ORG_CLAIM_PARAM)) return;
        url.searchParams.set(SUPREME_ORG_CLAIM_PARAM, currentOrgClaim);
        anchor.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
      } catch {
        // Never break a navigation over scoping; leave the anchor untouched.
      }
    },
    true,
  );
}

function installSupremeOrgClaimFetch(): void {
  if (fetchPatched || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  fetchPatched = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const rawUrl =
        typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const url = new URL(rawUrl, window.location.origin);
      const isApi = url.pathname === '/api' || url.pathname.startsWith('/api/');
      if (isApi && url.origin === window.location.origin && (currentOrgClaim || lastSyncedToken)) {
        const headers = new Headers(
          init?.headers ?? (input instanceof Request ? input.headers : undefined),
        );
        if (currentOrgClaim && !headers.has(SUPREME_ORG_CLAIM_HEADER)) {
          headers.set(SUPREME_ORG_CLAIM_HEADER, currentOrgClaim);
        }
        // SI-279: also carry this tab's own access token. The shared
        // supreme-token cookie can hold another tab's older token, which SI's
        // rotation may have invalidated — the proxy then answered 401
        // ORG_SCOPE ("organization scope unavailable") and run starts /
        // project creates failed intermittently. The proxy prefers the header
        // token over the cookie and validates it the same way.
        if (lastSyncedToken && !headers.has(SUPREME_TOKEN_HEADER)) {
          headers.set(SUPREME_TOKEN_HEADER, lastSyncedToken);
        }
        init = { ...(init ?? {}), headers };
      }
    } catch {
      // Scoping must never break a request; fall through untouched.
    }
    return originalFetch(input as RequestInfo, init);
  }) as typeof window.fetch;
}
